import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { useAuth } from '../contexts/AuthContext';

const ResetPassword = () => {
  const { register, handleSubmit, watch, formState: { errors } } = useForm();
  const { requestPasswordReset, verifyPasswordResetCode, resetPassword } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [step, setStep] = useState(1); // 1: Request, 2: Verify, 3: Reset
  const [email, setEmail] = useState('');
  const navigate = useNavigate();
  
  // Watch password for confirmation validation
  const password = watch('password', '');
  
  // Handle request password reset
  const handleRequestReset = async (data) => {
    try {
      setIsLoading(true);
      setError('');
      setSuccess('');
      
      await requestPasswordReset(data.email);
      
      setEmail(data.email);
      setSuccess('קוד אימות נשלח לכתובת האימייל שלך');
      setStep(2);
    } catch (err) {
      if (err.response?.data?.error) {
        setError(err.response.data.error);
      } else {
        setError('שגיאה בשליחת קוד איפוס. אנא נסה שנית.');
      }
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };
  
  // Handle verify reset code
  const handleVerifyCode = async (data) => {
    try {
      setIsLoading(true);
      setError('');
      setSuccess('');
      
      await verifyPasswordResetCode(email, data.verificationCode);
      
      setSuccess('קוד אומת בהצלחה');
      setStep(3);
    } catch (err) {
      if (err.response?.data?.error) {
        setError(err.response.data.error);
      } else {
        setError('שגיאה באימות קוד. אנא נסה שנית.');
      }
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };
  
  // Handle reset password
  const handleResetPassword = async (data) => {
    try {
      setIsLoading(true);
      setError('');
      setSuccess('');
      
      await resetPassword(data.password);
      
      setSuccess('הסיסמה אופסה בהצלחה');
      
      // Navigate to login page after a short delay
      setTimeout(() => {
        navigate('/login');
      }, 2000);
    } catch (err) {
      if (err.response?.data?.error) {
        setError(err.response.data.error);
      } else {
        setError('שגיאה באיפוס סיסמה. אנא נסה שנית.');
      }
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-transparent py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div className="text-center">
          <h1 className="text-3xl font-extrabold text-gray-900">איפוס סיסמה</h1>
          <p className="mt-2 text-sm text-gray-600">
            <Link to="/login" className="font-medium text-blue-600 hover:text-blue-500">
              חזרה להתחברות
            </Link>
          </p>
        </div>
        
        {/* Error Message */}
        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative" role="alert">
            <span className="block sm:inline">{error}</span>
          </div>
        )}
        
        {/* Success Message */}
        {success && (
          <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded relative" role="alert">
            <span className="block sm:inline">{success}</span>
          </div>
        )}
        
        {/* Step 1: Request Reset */}
        {step === 1 && (
          <form className="mt-8 space-y-6" onSubmit={handleSubmit(handleRequestReset)}>
            <div>
              <label htmlFor="email" className="form-label">אימייל</label>
              <input
                id="email"
                type="email"
                {...register('email', { 
                  required: 'אימייל הוא שדה חובה',
                  pattern: {
                    value: /\S+@\S+\.\S+/,
                    message: 'אנא הזן כתובת אימייל תקינה'
                  }
                })}
                className="form-input"
                placeholder="הזן את כתובת האימייל שלך"
                dir="ltr"
              />
              {errors.email && (
                <p className="text-red-500 text-xs mt-1">{errors.email.message}</p>
              )}
            </div>

            <div>
              <button
                type="submit"
                disabled={isLoading}
                className={`btn-primary w-full ${isLoading ? 'opacity-70 cursor-not-allowed' : ''}`}
              >
                {isLoading ? 'שולח קוד...' : 'שלח קוד איפוס'}
              </button>
            </div>
          </form>
        )}
        
        {/* Step 2: Verify Code */}
        {step === 2 && (
          <form className="mt-8 space-y-6" onSubmit={handleSubmit(handleVerifyCode)}>
            <div>
              <label htmlFor="verificationCode" className="form-label">קוד אימות</label>
              <input
                id="verificationCode"
                type="text"
                {...register('verificationCode', { 
                  required: 'קוד אימות הוא שדה חובה',
                  pattern: {
                    value: /^\d+$/,
                    message: 'קוד אימות חייב להכיל רק ספרות'
                  }
                })}
                className="form-input text-center text-2xl tracking-widest"
                placeholder="_ _ _ _ _ _"
                dir="ltr"
                maxLength={6}
              />
              {errors.verificationCode && (
                <p className="text-red-500 text-xs mt-1">{errors.verificationCode.message}</p>
              )}
            </div>

            <div className="flex flex-col space-y-3">
              <button
                type="submit"
                disabled={isLoading}
                className={`btn-primary w-full ${isLoading ? 'opacity-70 cursor-not-allowed' : ''}`}
              >
                {isLoading ? 'מאמת...' : 'אמת קוד'}
              </button>
              
              <button
                type="button"
                onClick={() => setStep(1)}
                className="btn-secondary w-full"
              >
                חזרה
              </button>
            </div>
          </form>
        )}
        
        {/* Step 3: Reset Password */}
        {step === 3 && (
          <form className="mt-8 space-y-6" onSubmit={handleSubmit(handleResetPassword)}>
            <div>
              <label htmlFor="password" className="form-label">סיסמה חדשה</label>
              <input
                id="password"
                type="password"
                {...register('password', { 
                  required: 'סיסמה היא שדה חובה',
                  minLength: {
                    value: 6,
                    message: 'סיסמה חייבת להכיל לפחות 6 תווים'
                  }
                })}
                className="form-input"
                placeholder="הזן סיסמה חדשה"
                dir="ltr"
              />
              {errors.password && (
                <p className="text-red-500 text-xs mt-1">{errors.password.message}</p>
              )}
            </div>
            
            <div>
              <label htmlFor="confirmPassword" className="form-label">אימות סיסמה</label>
              <input
                id="confirmPassword"
                type="password"
                {...register('confirmPassword', { 
                  required: 'אימות סיסמה הוא שדה חובה',
                  validate: value => value === password || 'הסיסמאות אינן תואמות'
                })}
                className="form-input"
                placeholder="הזן שוב את הסיסמה החדשה"
                dir="ltr"
              />
              {errors.confirmPassword && (
                <p className="text-red-500 text-xs mt-1">{errors.confirmPassword.message}</p>
              )}
            </div>

            <div className="flex flex-col space-y-3">
              <button
                type="submit"
                disabled={isLoading}
                className={`btn-primary w-full ${isLoading ? 'opacity-70 cursor-not-allowed' : ''}`}
              >
                {isLoading ? 'מאפס סיסמה...' : 'אפס סיסמה'}
              </button>
              
              <button
                type="button"
                onClick={() => setStep(2)}
                className="btn-secondary w-full"
              >
                חזרה
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default ResetPassword;
